import { useCallback, useRef, useState } from "react";
import type { PackageWithLinksResult } from "@models/ISchema";
import { usePackageWithLeaguesCreationService } from "@services/domains/packages";
import type { PackageWithLeaguesStoreApi } from "../../store/packageWithLeaguesStore";
import type { ValidationPresentation } from "../../types";
import {
  buildOrderedPackageWithLinksRequest,
  PackageConfigurationError,
  type OrderedPackageWithLinksRequest,
  toCommitRequest,
} from "../../utils/package-with-leagues-mappers";
import {
  getUnresolvedDiscardableCourseIds,
  hasBlockingValidationMessages,
  hasSubmissionValidations,
  mapSubmissionValidations,
  type SubmissionValidations,
} from "../../utils/package-with-leagues-submission";
import { resolveValidationMessage } from "../../utils/validation-message";
import useInferLinkCodes from "../use-infer-link-codes";

type JointCreationPhase = "idle" | "inferring-codes" | "validating" | "committing";
export type JointCreationStatus = "success" | "validation-errors" | "request-error" | "busy";

export interface JointCreationOutcome {
  status: JointCreationStatus;
  result?: PackageWithLinksResult;
  packageId?: string;
  discardedCourseIds?: string[];
}

interface UsePackageWithLeaguesCreationProps {
  scenarioId: string;
  originId: string;
  store: PackageWithLeaguesStoreApi;
  onSuccess?: (outcome: JointCreationOutcome) => void;
}

const requestErrorPresentation = (): ValidationPresentation => ({
  reason: "PACKAGE_WITH_LINKS_REQUEST_FAILED",
  message: resolveValidationMessage("PACKAGE_WITH_LINKS_REQUEST_FAILED"),
  scope: "global",
  discardable: false,
});

const configurationErrorPresentation = (
  error: PackageConfigurationError,
): ValidationPresentation => ({
  reason: error.reason,
  message: resolveValidationMessage(error.reason),
  scope: error.courseId ? "subject" : "global",
  discardable: false,
  ...(error.field ? { field: error.field } : {}),
});

export const usePackageWithLeaguesCreation = ({
  scenarioId,
  originId,
  store,
  onSuccess,
}: UsePackageWithLeaguesCreationProps) => {
  const { submitPackageWithLinks } = usePackageWithLeaguesCreationService();
  const { inferLinkCodes } = useInferLinkCodes({
    scenarioId,
    originId,
  });
  const [phase, setPhase] = useState<JointCreationPhase>("idle");
  const inFlight = useRef(false);

  const applyValidations = useCallback(
    (validations: SubmissionValidations, preserveExistingSubjects = false) => {
      const actions = store.getState();
      if (!preserveExistingSubjects) actions.clearValidationMessages();

      Object.entries(validations.byCourseId).forEach(([courseId, messages]) => {
        actions.setSubjectValidationMessages(courseId, messages);
      });
      actions.setGlobalValidationMessages(validations.global);
    },
    [store],
  );

  const executeRequest = useCallback(
    async (request: OrderedPackageWithLinksRequest) => {
      const result = await submitPackageWithLinks(scenarioId, originId, request.input);
      if (!result) throw new Error("PACKAGE_WITH_LINKS_EMPTY_RESPONSE");
      return result;
    },
    [originId, scenarioId, submitPackageWithLinks],
  );

  const prepareRequest = useCallback(
    async (dryRun: boolean, excludedCourseIds: ReadonlySet<string> = new Set()) => {
      const currentState = store.getState();
      const configurationsWithoutCode = currentState.subjects.filter(
        configuration =>
          !excludedCourseIds.has(configuration.subject.courseId) &&
          configuration.choice.kind === "create-new" &&
          !configuration.choice.inferredCode,
      );

      if (configurationsWithoutCode.length) {
        setPhase("inferring-codes");
        const codes = await inferLinkCodes(configurationsWithoutCode);
        Object.entries(codes).forEach(([courseId, code]) => {
          store.getState().setInferredLeagueCode(courseId, code);
        });
      }

      return buildOrderedPackageWithLinksRequest(store.getState(), dryRun, excludedCourseIds);
    },
    [inferLinkCodes, store],
  );

  const handleFailedResult = useCallback(
    (
      result: PackageWithLinksResult,
      request: OrderedPackageWithLinksRequest,
      preserveExistingSubjects = false,
    ): JointCreationOutcome => {
      const validations = mapSubmissionValidations(result, request.subjects);
      if (!hasSubmissionValidations(validations)) {
        validations.global.push({
          reason: "PACKAGE_WITH_LINKS_NOT_COMMITTED",
          message: resolveValidationMessage("PACKAGE_WITH_LINKS_NOT_COMMITTED"),
          scope: "global",
          discardable: false,
        });
      }
      applyValidations(validations, preserveExistingSubjects);
      return { status: "validation-errors", result };
    },
    [applyValidations],
  );

  const runFlow = useCallback(
    async (
      excludedCourseIds: ReadonlySet<string>,
      preserveExistingSubjects: boolean,
    ): Promise<JointCreationOutcome> => {
      const dryRunRequest = await prepareRequest(true, excludedCourseIds);
      setPhase("validating");
      const dryRunResult = await executeRequest(dryRunRequest);
      const dryRunValidations = mapSubmissionValidations(dryRunResult, dryRunRequest.subjects);

      if (hasSubmissionValidations(dryRunValidations)) {
        applyValidations(dryRunValidations, preserveExistingSubjects);
        return { status: "validation-errors", result: dryRunResult };
      }

      const commitRequest = toCommitRequest(dryRunRequest);
      setPhase("committing");
      const commitResult = await executeRequest(commitRequest);

      if (!commitResult.commited) {
        return handleFailedResult(commitResult, commitRequest, preserveExistingSubjects);
      }

      const outcome: JointCreationOutcome = {
        status: "success",
        result: commitResult,
        packageId: commitResult.payload?.package?.package?.id,
        discardedCourseIds: [...excludedCourseIds],
      };
      onSuccess?.(outcome);
      return outcome;
    },
    [applyValidations, executeRequest, handleFailedResult, onSuccess, prepareRequest],
  );

  const submit = useCallback(async (): Promise<JointCreationOutcome> => {
    if (inFlight.current) return { status: "busy" };

    inFlight.current = true;
    try {
      const state = store.getState();
      const hasBlockingMessages = hasBlockingValidationMessages(state);
      const unresolvedCourseIds = hasBlockingMessages
        ? new Set<string>()
        : getUnresolvedDiscardableCourseIds(state.validationMessagesByCourseId);

      const preserveExistingSubjects = unresolvedCourseIds.size > 0;
      if (!preserveExistingSubjects) state.clearValidationMessages();
      return await runFlow(unresolvedCourseIds, preserveExistingSubjects);
    } catch (submissionError) {
      if (submissionError instanceof PackageConfigurationError) {
        const actions = store.getState();
        const presentation = configurationErrorPresentation(submissionError);
        actions.clearValidationMessages();
        if (submissionError.courseId) {
          actions.setSubjectValidationMessages(submissionError.courseId, [presentation]);
        } else {
          actions.setGlobalValidationMessages([presentation]);
        }
        return { status: "validation-errors" };
      }

      store.getState().setGlobalValidationMessages([requestErrorPresentation()]);
      return { status: "request-error" };
    } finally {
      inFlight.current = false;
      setPhase("idle");
    }
  }, [runFlow, store]);

  return {
    submit,
    isLoading: phase !== "idle",
  };
};

export default usePackageWithLeaguesCreation;
